/**
 * The shell every signed-in screen sits in.
 *
 * Two decisions carry it.
 *
 * **The navigation is a rail, not a dashboard sidebar.** A producer opens this to look at one
 * master, so the screen belongs to the measurement, not to the chrome. The rail is 68 px with
 * labels under the glyphs and expands on wider viewports; below 900 px it becomes a bottom bar,
 * which is where a thumb is on a phone and where the native app already puts it. Same
 * destinations, same order, same glyphs on all three surfaces — the phone's tab bar, this rail
 * and the panel's — because a person who learns one has learned the others.
 *
 * **What a free account cannot reach is shown, locked, in place.** Not hidden, not a modal. The
 * research is blunt about why: every dashboard studied that hid its paid features taught users
 * the product was smaller than it is, and every one that interrupted with a modal was described
 * in reviews as nagging. A locked row that says what it would show is an advertisement that
 * costs nothing and annoys nobody.
 */

import { Link, useLocation } from 'react-router';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { useSession } from '@/lib/session';

interface Destination {
  to: string;
  label: string;
  glyph: ReactNode;
  /** When set, the destination needs this entitlement and is shown locked without it. */
  needs?: 'reference_slots' | 'album_tracks' | 'history';
}

/**
 * The glyphs are drawn here rather than imported from an icon set.
 *
 * Every one names a destination in this product's own terms — the gauge is the report, the
 * bars are the library, the two arcs are a comparison. A generic icon pack gives you a
 * house, a folder and a bar chart, which is what every other dashboard uses and what none of
 * them mean.
 */
const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const };

const DESTINATIONS: Destination[] = [
  {
    to: '/',
    label: 'Home',
    glyph: (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <path {...stroke} d="M4.6 16.5a8.5 8.5 0 1 1 14.8 0" />
        <path {...stroke} strokeWidth={2.4} d="M4.6 16.5A8.5 8.5 0 0 1 7 5.6" />
      </svg>
    ),
  },
  {
    // The product's primary action, in the rail. Before 2026-09-14 the only drop zone a
    // returning visitor could reach was behind Tracks → "Analyse files": the front page shows
    // the latest master once one exists, so the second upload was the thing nobody could find.
    to: '/analyse',
    label: 'Analyse',
    glyph: (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <path {...stroke} d="M12 16V5" />
        <path {...stroke} d="M7.5 9.5 12 5l4.5 4.5" />
        <path {...stroke} d="M5 19h14" />
      </svg>
    ),
  },
  {
    to: '/tracks',
    label: 'Tracks',
    glyph: (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <path {...stroke} d="M4 8h16M4 12h11M4 16h7" />
      </svg>
    ),
  },
  {
    to: '/references',
    label: 'References',
    needs: 'reference_slots',
    glyph: (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <path {...stroke} d="M7 18a6 6 0 0 1 0-12" />
        <path {...stroke} d="M17 6a6 6 0 0 1 0 12" />
        <path {...stroke} d="M12 4v16" strokeDasharray="2 3" />
      </svg>
    ),
  },
  {
    to: '/albums',
    label: 'Releases',
    needs: 'album_tracks',
    glyph: (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <rect {...stroke} x="4" y="5" width="16" height="14" rx="3" />
        <path {...stroke} d="M4 10h16" />
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'You',
    glyph: (
      <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
        <circle {...stroke} cx="12" cy="9" r="3.4" />
        <path {...stroke} d="M5.5 19a6.5 6.5 0 0 1 13 0" />
      </svg>
    ),
  },
];

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { entitlements } = useSession();

  const locked = (destination: Destination): boolean => {
    if (!destination.needs) return false;
    if (!entitlements) return true;
    const value = entitlements[destination.needs];
    return typeof value === 'number' ? value <= 0 : !value;
  };

  return (
    <div className="min-h-dvh bg-background text-foreground">
      {/* Skip link: the first thing a keyboard reaches, and the reason a rail of six links
          does not sit between the tab key and the measurement someone came to read. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-card focus:px-4 focus:py-2 focus:text-sm focus:shadow-lg"
      >
        Skip to the report
      </a>

      <div className="flex min-h-dvh">
        <nav
          aria-label="Sections"
          className="hidden shrink-0 flex-col gap-1 border-r border-border bg-card/40 px-2 py-4 md:flex md:w-[76px] lg:w-56 lg:px-3"
        >
          <Link to="/" className="mb-4 flex items-center gap-2.5 px-2 py-1" aria-label="RefBooth">
            <svg viewBox="0 0 24 24" width={26} height={26} aria-hidden="true">
              <path
                d="M4.6 16.5a8.5 8.5 0 1 1 14.8 0"
                fill="none"
                stroke="var(--rb-in-spec-ink)"
                strokeWidth={2.6}
                strokeLinecap="butt"
              />
            </svg>
            <span className="hidden text-[15px] font-semibold tracking-tight lg:inline">
              RefBooth
            </span>
          </Link>

          {DESTINATIONS.map((destination) => {
            const active = isActive(pathname, destination.to);
            const isLocked = locked(destination);
            return (
              <Link
                key={destination.to}
                to={destination.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'group flex items-center gap-3 rounded-control px-2.5 py-2.5 text-[13px] font-medium transition-colors',
                  'md:flex-col md:gap-1 md:px-1 md:py-2 md:text-[11px] lg:flex-row lg:gap-3 lg:px-2.5 lg:py-2.5 lg:text-[13px]',
                  active ? 'bg-tile text-ink' : 'text-ink2 hover:bg-tile/60 hover:text-ink',
                )}
              >
                <span className={cn(active && 'text-pass-ink')}>{destination.glyph}</span>
                <span className="flex items-center gap-1.5">
                  {destination.label}
                  {isLocked && (
                    <svg viewBox="0 0 24 24" width={12} height={12} aria-label="on a paid plan">
                      <path
                        {...stroke}
                        strokeWidth={2}
                        d="M8 10V7.5a4 4 0 0 1 8 0V10"
                      />
                      <rect {...stroke} strokeWidth={2} x="5.5" y="10" width="13" height="9" rx="2" />
                    </svg>
                  )}
                </span>
              </Link>
            );
          })}

          <div className="mt-auto hidden lg:block">
            <PlanBadge />
          </div>
        </nav>

        <main id="main" className="min-w-0 flex-1 pb-20 md:pb-0">
          {children}
        </main>
      </div>

      {/* Below the breakpoint the rail becomes a bottom bar — where a thumb is, and where the
          phone app already puts the same five destinations in the same order. */}
      <nav
        aria-label="Sections"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-card/95 backdrop-blur md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {DESTINATIONS.map((destination) => {
          const active = isActive(pathname, destination.to);
          return (
            <Link
              key={destination.to}
              to={destination.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium',
                active ? 'text-pass-ink' : 'text-ink2',
              )}
            >
              {destination.glyph}
              {destination.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * What plan this account is on, and the one place the upgrade lives in the chrome.
 *
 * Deliberately at the bottom of the rail rather than in a banner. A producer who has not paid
 * should be able to use the whole free product without being sold to on every screen; the locks
 * on the features themselves are the honest version of the same message.
 */
function PlanBadge() {
  const { entitlements, isSignedIn } = useSession();
  if (!entitlements) return null;

  if (!isSignedIn) {
    return (
      <Link
        to="/sign-in"
        className="block rounded-tile bg-pass-tint px-3 py-2.5 text-[12px] leading-snug text-pass-ink"
      >
        <span className="font-semibold">Keep this analysis</span>
        <br />
        Sign in and your report is saved.
      </Link>
    );
  }

  // Open testing: everything is unlocked for everyone, and the badge says so rather than
  // printing a plan nobody paid for. The link goes to the plan page, which explains the same.
  if (entitlements.plan_id === 'open') {
    return (
      <Link
        to="/settings/plan"
        className="block rounded-tile bg-pass-tint px-3 py-2.5 text-[12px] leading-snug text-pass-ink"
      >
        <span className="font-semibold">Open testing</span>
        <br />
        Every instrument is unlocked
      </Link>
    );
  }

  if (entitlements.plan_id === 'free') {
    return (
      <Link
        to="/settings/plan"
        className="block rounded-tile border border-border px-3 py-2.5 text-[12px] leading-snug text-ink2 transition-colors hover:border-pass-ink hover:text-ink"
      >
        <span className="font-semibold text-ink">Free plan</span>
        <br />
        Compare against references
      </Link>
    );
  }

  return (
    <div className="rounded-tile px-3 py-2.5 text-[12px] text-ink2">
      <span className="font-semibold text-ink">{entitlements.plan_name}</span>
    </div>
  );
}
